import React, { useState, useEffect } from 'react';
import { 
  FaBook, 
  FaClipboardList, 
  FaDollarSign, 
  FaCheckCircle,
  FaExclamationCircle,
  FaCalendarAlt,
  FaGraduationCap 
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import './MahasiswaDashboard.css';

const MahasiswaDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    currentSemester: '-',
    totalSKS: 0,
    ipk: 0,
    ips: 0,
    totalTagihan: 0,
    tagihanLunas: 0,
    jadwalHariIni: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // TODO: Replace with actual API call
      // const res = await axios.get('/api/mahasiswa/dashboard');
      // setDashboardData(res.data.data);
      
      // Mock data for now
      setTimeout(() => {
        setDashboardData({
          currentSemester: '2024/2025 Ganjil',
          totalSKS: 120,
          ipk: 3.45,
          ips: 3.67,
          totalTagihan: 5000000,
          tagihanLunas: 3500000,
          jadwalHariIni: [
            { 
              time: '08:00 - 10:00', 
              course: 'Keperawatan Dasar', 
              room: 'Lab A-201',
              lecturer: 'Dr. Ahmad'
            },
            { 
              time: '13:00 - 15:00', 
              course: 'Anatomi Fisiologi', 
              room: 'Ruang B-102',
              lecturer: 'Dr. Siti'
            }
          ]
        });
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const getAvatarUrl = (avatar) => {
    if (!avatar || avatar === 'default-avatar.png' || avatar === 'default-student.png') {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=random&size=200`;
    }
    
    if (avatar.includes('/')) {
      return `http://localhost:5000/uploads/${avatar}`;
    }
    
    return `http://localhost:5000/uploads/avatars/${avatar}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="mahasiswa-dashboard">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  const tagihanPercentage = dashboardData.totalTagihan > 0 
    ? (dashboardData.tagihanLunas / dashboardData.totalTagihan * 100).toFixed(0)
    : 0;

  return (
    <div className="mahasiswa-dashboard">
      {/* Header Profile */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="profile-section">
            <div className="profile-avatar-large">
              <img 
                src={getAvatarUrl(user?.avatar)}
                alt={user?.name}
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=random&size=200`;
                }}
              />
            </div>
            <div className="profile-details">
              <h1>Selamat Datang, {user?.name}</h1>
              <p className="nim-display">NIM: {user?.nim}</p>
              <p className="semester-display">
                <FaCalendarAlt /> {dashboardData.currentSemester}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-ipk">
          <div className="stat-icon">
            <FaGraduationCap />
          </div>
          <div className="stat-content">
            <h3>IPK</h3>
            <p className="stat-value">{dashboardData.ipk.toFixed(2)}</p>
            <span className="stat-label">Indeks Prestasi Kumulatif</span>
          </div>
        </div>

        <div className="stat-card stat-ips">
          <div className="stat-icon">
            <FaClipboardList />
          </div>
          <div className="stat-content">
            <h3>IPS</h3>
            <p className="stat-value">{dashboardData.ips.toFixed(2)}</p>
            <span className="stat-label">Semester Ini</span>
          </div>
        </div>

        <div className="stat-card stat-sks">
          <div className="stat-icon">
            <FaBook />
          </div>
          <div className="stat-content">
            <h3>Total SKS</h3>
            <p className="stat-value">{dashboardData.totalSKS}</p>
            <span className="stat-label">SKS Terpenuhi</span>
          </div>
        </div>

        <div className="stat-card stat-keuangan">
          <div className="stat-icon">
            <FaDollarSign />
          </div>
          <div className="stat-content">
            <h3>Pembayaran</h3>
            <p className="stat-value">{tagihanPercentage}%</p>
            <span className="stat-label">Lunas</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="content-grid">
        {/* Jadwal Hari Ini */}
        <div className="content-card jadwal-card">
          <div className="card-header">
            <h2>
              <FaCalendarAlt /> Jadwal Hari Ini
            </h2>
          </div>
          <div className="card-body">
            {dashboardData.jadwalHariIni.length > 0 ? (
              <div className="jadwal-list">
                {dashboardData.jadwalHariIni.map((jadwal, index) => (
                  <div key={index} className="jadwal-item">
                    <div className="jadwal-time">
                      <FaCalendarAlt />
                      <span>{jadwal.time}</span>
                    </div>
                    <div className="jadwal-details">
                      <h4>{jadwal.course}</h4>
                      <p>Ruang: {jadwal.room}</p>
                      <p className="lecturer">Dosen: {jadwal.lecturer}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <FaCheckCircle />
                <p>Tidak ada jadwal hari ini</p>
              </div>
            )}
          </div>
        </div>

        {/* Status Keuangan */}
        <div className="content-card keuangan-card">
          <div className="card-header">
            <h2>
              <FaDollarSign /> Status Keuangan
            </h2>
          </div>
          <div className="card-body">
            <div className="keuangan-summary">
              <div className="keuangan-item">
                <span className="label">Total Tagihan:</span>
                <span className="value">{formatCurrency(dashboardData.totalTagihan)}</span>
              </div>
              <div className="keuangan-item paid">
                <span className="label">Sudah Dibayar:</span>
                <span className="value">{formatCurrency(dashboardData.tagihanLunas)}</span>
              </div>
              <div className="keuangan-item unpaid">
                <span className="label">Sisa Tagihan:</span>
                <span className="value">
                  {formatCurrency(dashboardData.totalTagihan - dashboardData.tagihanLunas)}
                </span>
              </div>
              
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${tagihanPercentage}%` }}
                  ></div>
                </div>
                <span className="progress-label">{tagihanPercentage}% Lunas</span>
              </div>

              {tagihanPercentage < 100 && (
                <div className="alert-warning">
                  <FaExclamationCircle />
                  <span>Segera lunasi pembayaran Anda</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="content-card links-card">
          <div className="card-header">
            <h2>Menu Cepat</h2>
          </div>
          <div className="card-body">
            <div className="quick-links">
              <a href="/mahasiswa/krs" className="quick-link">
                <FaBook />
                <span>KRS</span>
              </a>
              <a href="/mahasiswa/khs" className="quick-link">
                <FaClipboardList />
                <span>KHS</span>
              </a>
              <a href="/mahasiswa/transkrip" className="quick-link">
                <FaGraduationCap />
                <span>Transkrip</span>
              </a>
              <a href="/mahasiswa/pembayaran" className="quick-link">
                <FaDollarSign />
                <span>Pembayaran</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MahasiswaDashboard;
